/**
 * Krok O8: jeden radkovy model pro seznam i tabulku zakazek. Obe zobrazeni
 * kresli z TEHOZ payloadu, co uz ma kanban (`GET /v1/cases` + davkove
 * `/v1/properties/covers`) — zadny dalsi dotaz na zakazky, zadny druhy zdroj
 * pravdy o nazvu (viz `lib/case-title.ts`).
 */

import type { KanbanCase } from './cases-api.js';
import type { CaseDisplayName } from './case-title.js';
import { daysInStage } from './case-stages.js';

/**
 * Krok DEMO1: stejny tvar jako `PropertyCover` bez `propertyId`/`mediaId`/
 * `caption` — karta potrebuje jen tohle. Krok UXV4a bod 4 odsud odebral
 * `municipality` (stred obce): obrazek nemovitosti ho uz nekresli nikde
 * (predloha UX v4 § Pravidla 3 „Nikdy střed obce"), takze ho nikdo necetl.
 */
export interface CoverInfo {
  /**
   * Krok UXF1 bod 2: nemovitost, ke ktere server dopocita obrazek z Mapy.com
   * (`…/cover/panorama.jpg`, `…/cover/aerial.jpg`). Do UXF1 kreslil mapovy
   * nahled klient ze `lat`/`lon`, takze id tu nebylo potreba.
   */
  readonly propertyId: string;
  readonly thumbUrl: string | null;
  readonly lat: number | null;
  readonly lon: number | null;
}

export interface CaseRow {
  readonly id: string;
  readonly name: string;
  /** Krok PRU1: ukazkova zakazka pruvodce — v seznamu nese stitek „Ukázka". */
  readonly isDemo: boolean;
  /** `title` atribut — vyplneny jen u zastupneho nazvu (nese interni id). */
  readonly nameHint: string | undefined;
  readonly stage: string;
  readonly type: string;
  /**
   * Krok UXR1: popis pod adresou v tabulce (mockup „stavební pozemek 1 731 m²").
   *
   * Krok UXD4 (nalez V4 UX auditu 21. 10.): JEN PRVNI RADEK. `cases.description`
   * je vicradkovy — prvni radek je souhrn, ktery formular zalozeni slozi
   * (cenova predstava, parcela, katastralni uzemi), za nim stoji „Zadání
   * makléře", tedy vlastni poznamka makléře. Do souhrnneho radku seznamu
   * patri souhrn; poznamka se cte v detailu zakazky, kde na ni je misto. Bez
   * toho se do sloupce Adresa slila cela poznamka („TEST PRUCHOD - zivy
   * prubeh pilotu…") a radek prestal byt citelny.
   */
  readonly description: string | null;
  /**
   * Cely popis — `title` radku, aby se zkracenim nic neztratilo. Nepovinny
   * zamerne: je to jen nazvuk pro mys, zadne zobrazeni na nem nestoji, a
   * povinnym polem by se prepisovaly cizi fixtury radku v jinych sadach.
   */
  readonly descriptionFull?: string | null | undefined;
  /** Krok UXR1: sloupec „Poslední změna" (`updatedAt` zakazky). */
  readonly changedAt: string;
  readonly ownerName: string | null;
  readonly days: number;
  readonly nextTaskTitle: string | null;
  readonly nextTaskDueAt: string | null;
  readonly attentionCount: number;
  readonly coverUrl: string | null;
  /** Krok DEMO1: mapovy nahled, kdyz `coverUrl` chybi (viz `CaseCover`/`KanbanCard`). */
  readonly lat: number | null;
  readonly lon: number | null;
  /** Krok UXF1 bod 2: nemovitost, ke ktere server dopocita obrazek z Mapy.com. */
  readonly propertyId: string | null;
}

export interface BuildCaseRowsInput {
  readonly cases: readonly KanbanCase[];
  readonly ownerName: (userId: string | null) => string | null;
  /** Nazev k zobrazeni — pocita ho stranka jednou pro vsechna zobrazeni i pro hledani. */
  readonly displayName: (item: KanbanCase) => CaseDisplayName;
  readonly coverByProperty: ReadonlyMap<string, CoverInfo>;
  readonly now: Date;
}

/** Prvni neprazdny radek popisu (krok UXD4, nalez V4). `null` = popis je prazdny. */
export function caseSummaryLine(description: string | null): string | null {
  if (description === null) return null;
  const first = description
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line !== '');
  return first ?? null;
}

export function buildCaseRows(input: BuildCaseRowsInput): readonly CaseRow[] {
  return input.cases.map((item) => {
    const display = input.displayName(item);
    const cover = item.propertyId === null ? undefined : input.coverByProperty.get(item.propertyId);
    return {
      id: item.id,
      name: display.text,
      isDemo: item.isDemo,
      nameHint: display.hint,
      stage: item.stage,
      type: item.type,
      description: caseSummaryLine(item.description),
      descriptionFull: item.description,
      changedAt: item.updatedAt,
      ownerName: input.ownerName(item.ownerUserId),
      days: daysInStage(item.stageEnteredAt, input.now),
      nextTaskTitle: item.nextTaskTitle,
      nextTaskDueAt: item.nextTaskDueAt,
      attentionCount: item.attentionCount,
      coverUrl: cover?.thumbUrl ?? null,
      lat: cover?.lat ?? null,
      lon: cover?.lon ?? null,
      propertyId: item.propertyId,
    };
  });
}

/** Razeni tabulky. Klice odpovidaji `key` sloupcu tabulky zakazek. */
export type CaseSortField = 'name' | 'days' | 'dueAt' | 'changedAt';

export interface CaseSort {
  readonly field: CaseSortField;
  readonly dir: 'asc' | 'desc';
}

function compareField(a: CaseRow, b: CaseRow, field: CaseSortField): number {
  if (field === 'days') return a.days - b.days;
  if (field === 'changedAt') return a.changedAt.localeCompare(b.changedAt);
  if (field === 'dueAt') {
    // Zakazky bez terminu jsou vzdy dole (i pri sestupnem razeni) — stejne
    // jako `nulls last` na serverovych seznamech.
    if (a.nextTaskDueAt === null) return b.nextTaskDueAt === null ? 0 : Number.POSITIVE_INFINITY;
    if (b.nextTaskDueAt === null) return Number.NEGATIVE_INFINITY;
    return a.nextTaskDueAt.localeCompare(b.nextTaskDueAt);
  }
  return a.name.localeCompare(b.name, 'cs');
}

/**
 * Lokalni razeni nactene stranky (max `CASE_PAGE_LIMIT` zakazek — server je
 * uz poslal, dalsi request by nic nepridal). `null` = poradi z API.
 */
export function sortCaseRows(rows: readonly CaseRow[], sort: CaseSort | null): readonly CaseRow[] {
  if (sort === null) return rows;
  const factor = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const primary = compareField(a, b, sort.field);
    if (!Number.isFinite(primary)) return primary > 0 ? 1 : -1;
    if (primary !== 0) return primary * factor;
    return a.name.localeCompare(b.name, 'cs') || a.id.localeCompare(b.id);
  });
}
